import React from 'react';

import {
  Button,
  Caption,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  Stack,
  Text,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';

import type { DocumentTemplate } from './useDocumentTemplates';

export type TemplateRowProps = {
  template: DocumentTemplate;
  isGenerating: boolean;
  onSelect: (t: DocumentTemplate) => void;
  onEdit: (t: DocumentTemplate) => void;
  onDelete: (t: DocumentTemplate) => void;
};

export const TemplateRow: React.FC<TemplateRowProps> = ({
  template,
  isGenerating,
  onSelect,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const isPdf = template.kind === 'PDF_FORM';
  const isUserTemplate = !template.isSystemDefault;
  const fieldCount = Array.isArray(template.fieldMetadata)
    ? template.fieldMetadata.length
    : null;

  return (
    <Stack
      className="group rounded-md px-2 py-2.5 transition-colors hover:bg-bg-tertiary"
      direction="horizontal"
      align="center"
    >
      <button
        type="button"
        className="flex flex-1 items-center gap-3 text-left disabled:opacity-50"
        disabled={isGenerating}
        onClick={() => onSelect(template)}
      >
        <Icon
          name={isPdf ? 'FileText' : 'Pencil'}
          size="lg"
          className="text-text-tertiary"
        />
        {isPdf ? (
          <Stack gap={0}>
            <Text inline weight="medium">
              {template.name}
            </Text>
            <Caption inline size="2xs" color="tertiary">
              {t('appointmentTools.documents.fillableForm')}
              {fieldCount != null &&
                ` · ${
                  fieldCount === 1
                    ? t('appointmentTools.documents.fieldsCountOne', {
                        count: fieldCount,
                      })
                    : t('appointmentTools.documents.fieldsCountMany', {
                        count: fieldCount,
                      })
                }`}
            </Caption>
          </Stack>
        ) : (
          <Text inline>{template.name}</Text>
        )}
      </button>
      {isPdf && (
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 text-text-indigo"
          disabled={isGenerating}
          onClick={() => onSelect(template)}
        >
          {t('appointmentTools.documents.use')}
          <Icon name="ArrowRight" size="auto" />
        </Button>
      )}
      {isUserTemplate && (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 text-text-tertiary"
              aria-label={t('appointmentTools.documents.templateOptions')}
            >
              <Icon name="EllipsisVertical" size="auto" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => onEdit(template)}>
              <Icon name="Pencil" size="lg" className="mr-2" />
              {t('appointmentTools.documents.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-text-negative"
              onSelect={() => onDelete(template)}
            >
              <Icon name="Trash" size="lg" className="mr-2" />
              {t('appointmentTools.documents.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </Stack>
  );
};
